import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { map } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingCreditDetails } from '../../models/casing-credit-details.model';
import { CreateCasingCredit } from '../../models/create-casing-credit.model';
import { MatTableDataSource } from '@angular/material/table';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';

@Component({
  selector: 'app-casing-credit-create',
  templateUrl: './casing-credit-create.component.html',
  styleUrl: './casing-credit-create.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditCreateComponent implements OnInit {
  casingCreditDetailsFormGroup: FormGroup;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingCreditDetails>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);

  casingCreditDetails: CasingCreditDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  saveEnabled = false;
  isCreateInProgress = false;

  repairGridList: string[] = [];
  // Initialize Data source for table
  dataSource: MatTableDataSource<CasingCreditDetails>;

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {
      casingSize: { cell: (row) => row.size, header: this.translationService.translate('casingSizeText') },
      description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
      baseCredit: { cell: 'baseCredit', header: this.translationService.translate('baseCreditText') },
      repairGrid: {
        cell: 'repairGridDropdown',
        header: this.translationService.translate('repairGridText')
      }
    }
  };

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.productDataService.getCasingCreditDetailsForCasingCreditCreate().pipe(
      map((response: any) => {
        this.casingCreditDetails = response.data || [];
        this.pagination = {
          totalRecords: this.casingCreditDetails.length,
          currentPage: 1,
          nextPage: null,
          previousPage: null,
          totalPages: 1
        };
        this.updateDisplayedRowsForm(this.casingCreditDetails);
        return { items: this.casingCreditDetails, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.casingCreditDetailsFormGroup = this.fb.group({
      code: '',
      description: ''
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getCasingCreditDetailsForCasingCreditCreate().subscribe((response: any) => {
      this.casingCreditDetails = response.data || [];
      this.updateDisplayedRowsForm(this.casingCreditDetails);
      this.dataSource = new MatTableDataSource(this.casingCreditDetails);
      this.cdr.markForCheck();
    });
    this.productDataService.getCasingCreditRepairGridSearchFilters().subscribe((response: any) => {
      this.repairGridList = response.data.repairGrids || [];
    });
  }

  createRowForm(row: CasingCreditDetails): FormGroup {
    return this.fb.group({
      size: [row.size],
      description: [row.description],
      baseCredit: [row.baseCredit],
      repairGrid: [row.repairGrid]
    });
  }

  updateDisplayedRowsForm(rows: any[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: CasingCreditDetails, controlName: string): FormControl {
    const index = this.casingCreditDetails.findIndex((row) => row.casingSizeFk === item.casingSizeFk); // Use appropriate unique key
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  cancel(): void {
    this.router.navigate(['/home/productData/casingCredit']);
  }

  save(): void {
    if (!this.saveEnabled) {
      return;
    }
    const codeControl = this.casingCreditDetailsFormGroup.get('code');
    const noCodeInlineErrors = codeControl.valid && !codeControl.errors;
    const descriptionControl = this.casingCreditDetailsFormGroup.get('description');
    const noDescriptionInlineErrors = descriptionControl.valid && !descriptionControl.errors;

    if (!codeControl.value) {
      codeControl.markAsTouched();
      codeControl.setErrors({ required: true });
      this.cdr.markForCheck();
    }

    if (!descriptionControl.value) {
      descriptionControl.markAsTouched();
      descriptionControl.setErrors({ required: true });
      this.cdr.markForCheck();
    }
    const code = this.casingCreditDetailsFormGroup.get('code').value;
    const description = this.casingCreditDetailsFormGroup.get('description').value;
    if (!code || !description || !noCodeInlineErrors || !noDescriptionInlineErrors) {
      return;
    }

    this.isCreateInProgress = true;
    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.casingCreditDetails.length; i++) {
      const original = this.casingCreditDetails[i];
      const current = formArray.at(i).value;
      // ensure baseCredit is numeric and default to 0 when empty/null
      const normalizedBaseCredit = current.baseCredit === null || current.baseCredit === '' ? 0 : current.baseCredit;
      modifiedRows.push({
        ...current,
        baseCredit: normalizedBaseCredit,
        casingCreditDetailId: original.casingCreditDetailId,
        casingSizeFk: original.casingSizeFk
      });
    }
    const payload: CreateCasingCredit = {
      code: this.casingCreditDetailsFormGroup.get('code').value,
      description: this.casingCreditDetailsFormGroup.get('description').value,
      casingCreditDetails: modifiedRows
    };
    this.productDataService.createCasingCredit(payload).subscribe({
      next: () => {
        this.isCreateInProgress = false;
        setTimeout(() => {
          this.router.navigate(['/home/productData/casingCredit']);
        }, 2000);
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
      },
      error: () => {
        this.isCreateInProgress = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.critical,
          content: this.translationService.translate('criticalErrorAlertText')
        } as Alert);
      }
    });
  }

  checkIfCasingCreditCodeExists() {
    const casingCreditCode = this.casingCreditDetailsFormGroup.get('code').value;
    this.productDataService.casingCreditCodeExists(casingCreditCode).subscribe((exists) => {
      if (exists) {
        this.casingCreditDetailsFormGroup.get('code').setErrors({ exists: true });
      } else {
        this.casingCreditDetailsFormGroup.get('code').setErrors(null);
      }
    });
  }

  /**
   * Called on keydown to mark deletion intent for environments where inputType is not available.
   */
  handleKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      input.dataset.isDeleting = 'true';
    }
  }

  /**
   * Restrict input to at most two digits after decimal point.
   * Allows empty string so user can delete all characters (no forced '0').
   * Preserves caret position and keeps FormControl in sync.
   * If the user is deleting (inputType starts with 'delete' or keydown marked it), allow the cleaned result to become empty
   * so repeated backspace can clear the field.
   */
  restrictTwoDecimalPlaces(event: Event, item: CasingCreditDetails): void {
    const input = event.target as HTMLInputElement;
    if (!input) {
      return;
    }

    const inputEventType = (event as any)?.inputType ?? '';
    const isDeleting = input.dataset.isDeleting === 'true' || inputEventType.startsWith('delete');

    const originalValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? originalValue.length;

    const cleanValue = (val: string) => {
      if (val === '') {
        return '';
      }
      let v = val.replace(/[^0-9.]/g, ''); // remove invalid chars
      v = v.replace(/(\..*)\./g, '$1'); // keep only first dot
      if (v === '.') {
        return '0.'; // keep sensible "0." when user types dot
      }
      if (v.startsWith('.')) {
        v = '0' + v;
      }
      if (v.includes('.')) {
        const [intPart, fracPart] = v.split('.');
        v = intPart + '.' + (fracPart ? fracPart.slice(0, 2) : '');
      } else {
        // remove leading zeros only if followed by another digit (so "0" stays "0")
        v = v.replace(/^0+(\d)/, '$1');
        // do NOT force v = '0' if result becomes empty — allow empty
      }
      return v;
    };

    let newValue = cleanValue(originalValue);

    // If deletion intent and the cleaned value is '0', allow it to become empty so backspace can clear.
    if (isDeleting && newValue === '0') {
      newValue = '';
    }

    const originalPrefix = originalValue.slice(0, selectionStart);
    let cleanedPrefix = cleanValue(originalPrefix);
    if (isDeleting && cleanedPrefix === '0') {
      cleanedPrefix = '';
    }

    // Clear deletion flag so it doesn't affect next non-deleting input
    if (input.dataset.isDeleting) {
      delete input.dataset.isDeleting;
    }

    // If nothing changed, avoid touching the input (prevents caret reset)
    if (newValue === originalValue) {
      try {
        const control = this.getControl(item, 'baseCredit');
        if (control && control.value !== (newValue === '' ? null : newValue)) {
          control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
        }
      } catch {
        // ignore
      }
      return;
    }

    const newCaretPos = Math.min(cleanedPrefix.length, newValue.length);
    input.value = newValue;
    try {
      input.setSelectionRange(newCaretPos, newCaretPos);
    } catch {
      // ignore
    }

    try {
      const control = this.getControl(item, 'baseCredit');
      if (control) {
        control.setValue(newValue === '' ? null : newValue, { emitEvent: false });
      }
    } catch {
      // ignore
    }

    this.cdr.markForCheck();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.saveEnabled) {
      $event.returnValue = true;
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.casingCreditDetails);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters({}, true);
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.saveEnabled) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }
}
